import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { RoaryChat, isRestoredConversationWaiting } from './RoaryChat';
import { RoaryProgress } from './RoaryProgress';
import { RoaryComplete } from './RoaryComplete';
import { MarshmallowSidebar } from './MarshmallowSidebar';
import { MarshmallowPacksModal } from './MarshmallowPacksModal';
import { MarshmallowSkewer } from './MarshmallowSkewer';
import { RoaryImport } from './Import/RoaryImport';
import { ImportLayoutStep } from './Import/ImportLayoutStep';
import { useRoary } from '../../hooks/useRoary';
import { viewAfterProgress } from './roaryBuildView';
import { useMarshmallows } from '../../hooks/useMarshmallows';
import roaryMascot from '/assets/Roary-mascot.png';

interface RoaryModeProps {
  isOpen: boolean;
  onClose: () => void;
  onSiteCreated?: () => void;
  siteId?: string;
  mode?: 'site' | 'page';
  // Show the "Import an existing website" choice on the greeting.
  // The dashboard turns this on for every signed-in user. Defaults to false.
  allowImport?: boolean;
  // Regular users can build a new site or import one. Improve and questions stay
  // visible but disabled. Admins leave this off.
  importOnly?: boolean;
}

// Dragon scale pattern background
const DragonScaleBackground: React.FC = () => (
  <div
    className="absolute inset-0 opacity-[0.03]"
    style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0C13.4 0 0 13.4 0 30c0-16.6 13.4-30 30-30zm0 60C46.6 60 60 46.6 60 30c0 16.6-13.4 30-30 30z' fill='%23f97316' fill-opacity='0.5'/%3E%3C/svg%3E")`,
      backgroundSize: '30px 30px',
    }}
  />
);

// Ambient glow effect
const AmbientGlow: React.FC = () => (
  <>
    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
  </>
);

type ViewState = 'chat' | 'progress' | 'complete' | 'import' | 'choose_layout';

export const RoaryMode: React.FC<RoaryModeProps> = ({
  isOpen,
  onClose,
  onSiteCreated,
  siteId,
  mode = 'site',
  allowImport = false,
  importOnly = false,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [viewState, setViewState] = useState<ViewState>('chat');
  const [showPacksModal, setShowPacksModal] = useState(false);
  const continuedBuild = useRef(false);

  const marshmallows = useMarshmallows();

  const {
    sessionId,
    messages,
    isLoading,
    isStreaming,
    isThinking,
    currentToolName,
    error,
    operations,
    completionData,
    buildProgress,
    phase,
    chooseTemplateSignal,
    startSession,
    sendMessage,
    handleQuickReply,
    cancelSession,
    clearError,
    restoreSession,
  } = useRoary({
    onMarshmallowUpdate: marshmallows.onMarshmallowUpdate,
    onMarshmallowDepleted: marshmallows.onMarshmallowDepleted,
    allowImport,
    importOnly,
  });

  // Handle open animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Trigger animation after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsStarted(false);
        setViewState('chat');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Auto-start session when opened (try restoring a saved session first)
  const handleStart = useCallback(async () => {
    if (!isStarted && !sessionId) {
      // Try to restore a previous session from memory
      const restoredSession = restoreSession(mode);
      if (restoredSession) {
        setIsStarted(true);
        return;
      }

      // No previous session, start fresh
      try {
        await startSession(siteId, mode);
        setIsStarted(true);
      } catch {
        // Error is handled in the hook
      }
    }
  }, [isStarted, sessionId, siteId, mode, startSession, restoreSession]);

  useEffect(() => {
    if (isOpen && !isStarted && !sessionId) {
      handleStart();
    }
  }, [isOpen, isStarted, sessionId, handleStart]);

  // The layout grid opens after the business facts are confirmed.
  useEffect(() => {
    if (chooseTemplateSignal > 0) {
      setViewState('choose_layout');
    }
  }, [chooseTemplateSignal]);

  // Watch for operations or an active build plan to show progress view
  useEffect(() => {
    if (
      viewState === 'chat' &&
      isStreaming &&
      (operations.length > 0 || (buildProgress && buildProgress.pages.length > 0))
    ) {
      setViewState('progress');
    }
  }, [operations, buildProgress, viewState, isStreaming]);

  // A full-site plan stays on the working screen and builds straight through.
  // Other work, such as a single page edit, still returns to the chat.
  useEffect(() => {
    if (viewState !== 'progress') return;
    const next = viewAfterProgress({
      isStreaming,
      phase,
      hasCompletion: Boolean(completionData),
      hasError: Boolean(error),
      operations,
      continuedBuild: continuedBuild.current,
    });
    if (next.continueBuild) {
      continuedBuild.current = true;
      sendMessage('Yes, build it.');
      return;
    }
    if (next.view !== 'progress') {
      setViewState(next.view);
    }
  }, [phase, completionData, error, operations, viewState, isStreaming, sendMessage]);

  // Handle new session
  const handleNewSession = async () => {
    continuedBuild.current = false;
    setViewState('chat');
    try {
      await cancelSession();
    } catch {
      // The saved chat is cleared either way.
    }
    try {
      await startSession(siteId, mode);
      setIsStarted(true);
    } catch {
      // Error is handled in the hook
    }
  };

  const cachedAttempt = isRestoredConversationWaiting(messages);

  // Handle close
  const handleClose = () => {
    // If a site was created, notify parent to refresh
    if (completionData?.siteId && onSiteCreated) {
      onSiteCreated();
    }
    onClose();
  };

  // Handle continue after completion
  const handleContinue = () => {
    setViewState('chat');
  };

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed inset-0 z-50
        transition-all duration-300 ease-out
        ${isAnimating ? 'opacity-100' : 'opacity-0'}
      `}
    >
      {/* Backdrop - deep navy/black */}
      <div
        className={`
          absolute inset-0 bg-[#0a0a1a]
          transition-all duration-500
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
        `}
      />

      {/* Dragon scale pattern */}
      <DragonScaleBackground />

      {/* Ambient glow */}
      <AmbientGlow />

      {/* Main content container */}
      <div
        className={`
          relative z-10 h-full flex flex-col
          transition-all duration-500 ease-out
          ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
        `}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden">
              <img src={roaryMascot} alt="Roary" className="w-14 h-14 object-contain" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-lg">Roary</h1>
              <p className="text-gray-500 text-sm">Your AI website builder</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile marshmallow badge */}
            {!marshmallows.isLoading && (
              <div className="lg:hidden flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/80 text-xs">
                <MarshmallowSkewer className="w-4 h-4" />
                <span
                  className={marshmallows.isDepleted ? 'text-red-400 font-medium' : 'text-gray-300'}
                >
                  {marshmallows.remaining}/{marshmallows.total}
                </span>
              </div>
            )}

            {/* Start over stays in the header for the whole session. */}
            {sessionId && (
              <button
                onClick={handleNewSession}
                disabled={isLoading || isStreaming}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Start over"
                aria-label="Start over"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Start over
              </button>
            )}

            {/* Close button */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Exit Roary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Content area — sidebar + main */}
        <div className="flex-1 overflow-hidden flex">
          {/* Marshmallow Sidebar */}
          <MarshmallowSidebar
            remaining={marshmallows.remaining}
            total={marshmallows.total}
            burned={marshmallows.lastBurned}
            resetDate={marshmallows.resetDate}
            history={marshmallows.history}
            isDepleted={marshmallows.isDepleted}
            isLoading={marshmallows.isLoading}
            onBurnAnimationEnd={marshmallows.clearLastBurned}
            onBuyMore={() => setShowPacksModal(true)}
          />

          {/* Main content */}
          <div className="flex-1 overflow-hidden">
            {viewState === 'chat' && (
              <RoaryChat
                messages={messages}
                isLoading={isLoading}
                isStreaming={isStreaming}
                isThinking={isThinking}
                currentToolName={currentToolName}
                error={error}
                phase={phase}
                onSendMessage={sendMessage}
                onQuickReply={reply => {
                  // Import is a whole-site, admin-only operation. Only route
                  // to the dedicated Import view when the caller has flagged
                  // the session as admin-context AND we're in site mode.
                  if (reply.value === 'import' && mode === 'site' && allowImport) {
                    setViewState('import');
                    return;
                  }
                  if (reply.value === 'choose-layout') {
                    setViewState('choose_layout');
                    return;
                  }
                  handleQuickReply(reply);
                }}
                onCancel={cancelSession}
                onClearError={clearError}
                darkMode
                isDepleted={marshmallows.isDepleted}
                cachedAttempt={cachedAttempt}
                onStartOver={handleNewSession}
              />
            )}

            {viewState === 'choose_layout' && (
              <ImportLayoutStep
                title="Choose a template"
                intro="Pick the template for this site. You'll confirm the pages before anything is built."
                onSelect={template => {
                  setViewState('chat');
                  sendMessage(
                    `I'll use the "${template.name}" template.\ntemplateId: ${template.id}`
                  );
                }}
                onClose={() => setViewState('chat')}
              />
            )}

            {viewState === 'import' && mode === 'site' && allowImport && (
              <RoaryImport onClose={() => setViewState('chat')} />
            )}

            {viewState === 'progress' && (
              <RoaryProgress
                operations={operations}
                isStreaming={isStreaming}
                currentToolName={currentToolName}
                buildProgress={buildProgress}
              />
            )}

            {viewState === 'complete' && completionData && (
              <RoaryComplete
                completionData={completionData}
                onContinue={handleContinue}
                onClose={handleClose}
              />
            )}
          </div>
        </div>
      </div>

      {/* Marshmallow Packs Modal */}
      <MarshmallowPacksModal isOpen={showPacksModal} onClose={() => setShowPacksModal(false)} />
    </div>
  );
};
